"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

// Create Context
const ClientsContext = createContext();

// Custom hook for easy use
export const useClients = () => useContext(ClientsContext);

// Provider Component
export const ClientsProvider = ({ children }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ✅ Get token from localStorage
  const getToken = () => localStorage.getItem("token");

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // ✅ Fetch Clients
  const fetchClients = async () => {
    const token = getToken();
    if (!token) return;

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${BASE_URL}/clients`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        setError("Failed to fetch clients");
        return;
      }

      const data = await parseJson(response);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      setClients(data.data || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setError("Error fetching clients");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Create Client (supports image upload)
  const createClient = async (clientData) => {
    try {
      const formData = new FormData();

      Object.entries(clientData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.id || null;

      if (userId) {
        formData.append("create_audit_id", userId);
      }

      const response = await fetch(`${BASE_URL}/clients`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: formData,
      });

      const result = await parseJson(response);

      if (!response.ok || !result) {
        return {
          success: false,
          message: result?.message || "Something went wrong",
        };
      }

      if (result.status === 201) {
        await fetchClients();
        toast.success(result.message || "Client created successfully");

        return {
          success: true,
          message: result.message || "Client created successfully",
          data: result,
        };
      }

      return {
        success: false,
        message: result.message || "Something went wrong",
      };
    } catch (error) {
      console.error("❌ Error creating client:", error);

      return {
        success: false,
        message: error.message || "Error creating client",
      };
    }
  };

  // ✅ Update Client
  const updateClient = async (id, clientData) => {
    try {
      const formData = new FormData();

      for (const key in clientData) {
        if (clientData[key] !== null && clientData[key] !== undefined) {
          formData.append(key, clientData[key]);
        }
      }

      const response = await fetch(`${BASE_URL}/clients/${id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: formData,
      });

      const data = await parseJson(response);

      if (!response.ok || !data) {
        return {
          success: false,
          message: data?.message || "Update failed",
        };
      }

      setClients((prev) =>
        prev.map((client) =>
          client.id === id ? { ...client, ...clientData } : client
        )
      );

      await fetchClients();

      toast.success(data.message || "Client updated successfully");

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error("Error updating client:", error);

      return {
        success: false,
        message: "Network or server error",
      };
    }
  };

  // ✅ Delete Client
  const deleteClient = async (id) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const delete_audit_id = user?.id || null;

      const response = await fetch(`${BASE_URL}/clients/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ delete_audit_id }),
      });

      const data = await parseJson(response);

      if (!response.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to delete client",
        };
      }

      if (data.status === 200 || data.success) {
        setClients((prev) => prev.filter((c) => c.id !== id));

        toast.success(data.message || "Client deleted successfully");

        return {
          success: true,
          message: data.message || "Client deleted successfully",
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete client",
      };
    } catch (error) {
      console.error("Delete error:", error);

      return {
        success: false,
        message: error.message || "Error deleting client",
      };
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  return (
    <ClientsContext.Provider
      value={{
        clients,
        loading,
        error,
        createClient,
        updateClient,
        deleteClient,
        fetchClients,
      }}
    >
      {children}
    </ClientsContext.Provider>
  );
};